import { useRef, useState } from "react";
import { ArrowLeft, Download, FileSpreadsheet, UploadCloud, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const FIELDS = {
  customers: [
    { key: "name", label: "Name", required: true },
    { key: "company", label: "Company" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "address", label: "Address" },
    { key: "tax_id", label: "GSTIN / Tax ID" },
    { key: "balance", label: "Opening balance" },
  ],
  inventory: [
    { key: "name", label: "Product name", required: true },
    { key: "sku", label: "SKU" },
    { key: "category", label: "Category" },
    { key: "price", label: "Price" },
    { key: "cost", label: "Cost" },
    { key: "stock", label: "Stock" },
    { key: "reorder_level", label: "Reorder level" },
    { key: "unit", label: "Unit" },
  ],
  suppliers: [
    { key: "name", label: "Name", required: true },
    { key: "company", label: "Company" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "address", label: "Address" },
    { key: "payment_terms", label: "Payment terms" },
  ],
};

const META = {
  customers: { label: "Customers", collection: "customers", sample: "Maya Foods,Maya Foods Pvt Ltd,accounts@mayafoods.in,+91 98765 22011,18 Kalina Rd Mumbai,27AAACM8899L1Z2,124500" },
  inventory: { label: "Products", collection: "products", sample: "Bamboo Desk Organizer,FS-104,Office,1299,690,42,10,piece" },
  suppliers: { label: "Suppliers", collection: "suppliers", sample: "Anaya Timbers,Anaya Timbers LLP,orders@anayatimbers.in,+91 90080 22110,Peenya Bengaluru,Net 30" },
};

const normalize = (value) => String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");

const SYNONYMS = {
  name: ["name", "fullname", "customer", "client", "supplier", "product", "productname", "item", "title"],
  company: ["company", "business", "organisation", "organization", "firm"],
  email: ["email", "mail", "emailid", "emailaddress", "e"],
  phone: ["phone", "mobile", "contact", "phoneno", "phonenumber", "mobileno", "cell", "tel", "telephone"],
  address: ["address", "location", "city", "addr"],
  tax_id: ["taxid", "gstin", "gst", "gstno", "tax", "vat", "pan"],
  balance: ["balance", "opening", "openingbalance", "outstanding", "due", "amount"],
  sku: ["sku", "code", "itemcode", "productcode", "barcode"],
  category: ["category", "type", "group", "class"],
  price: ["price", "rate", "sellingprice", "sp", "mrp", "unitprice"],
  cost: ["cost", "buyprice", "purchaseprice", "cp", "costprice"],
  stock: ["stock", "quantity", "qty", "onhand", "instock", "units"],
  reorder_level: ["reorderlevel", "reorder", "minstock", "reorderpoint", "threshold"],
  unit: ["unit", "uom", "measure"],
  payment_terms: ["paymentterms", "terms", "credit", "creditterms"],
};

const guessMapping = (fields, headers) => {
  const map = {};
  const used = new Set();
  fields.forEach((field) => {
    const candidates = SYNONYMS[field.key] || [field.key];
    const target = normalize(field.key);
    const alt = normalize(field.label);
    const match = headers.find((h) => {
      if (used.has(h)) return false;
      const n = normalize(h);
      return candidates.includes(n) || n === target || n === alt || candidates.some((c) => n === c) || (n.length > 2 && candidates.some((c) => n.includes(c) || c.includes(n)));
    });
    if (match) {
      map[field.key] = match;
      used.add(match);
    }
  });
  return map;
};

export default function ImportModal({ type, onClose, onImported }) {
  const meta = META[type] || META.customers;
  const fields = FIELDS[type] || FIELDS.customers;
  const [stage, setStage] = useState("choose");
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [sampleRows, setSampleRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const downloadTemplate = () => {
    const csv = `${fields.map((f) => f.key).join(",")}\n${meta.sample}\n`;
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${meta.collection}-template.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const onFile = async (event) => {
    const chosen = event.target.files?.[0];
    if (!chosen) return;
    setFile(chosen);
    setBusy(true);
    try {
      const body = new FormData();
      body.append("file", chosen);
      const { data } = await api.post(`/import/${meta.collection}/preview`, body);
      setHeaders(data.headers);
      setSampleRows(data.sample_rows);
      setTotalRows(data.total_rows);
      setMapping(guessMapping(fields, data.headers));
      setStage("map");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not read this CSV file");
      setFile(null);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const runImport = async () => {
    if (!mapping.name) {
      toast.error("Map the required Name column before importing");
      return;
    }
    setBusy(true);
    try {
      const body = new FormData();
      body.append("file", file);
      body.append("mapping", JSON.stringify(mapping));
      const { data } = await api.post(`/import/${meta.collection}`, body);
      setResult(data);
      setStage("done");
      toast.success(`Imported ${data.imported} ${meta.label.toLowerCase()}`);
      onImported?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not import this file");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <section className="create-modal import-modal-shell" role="dialog" aria-modal="true" data-testid="import-modal">
        <div className="modal-heading">
          <div>
            <p className="eyebrow">FLOWSPHERE / IMPORT</p>
            <h2>Import {meta.label}</h2>
            <p>{stage === "map" ? "Match your CSV columns to FlowSphere fields, then import." : `Upload a CSV file to add many ${meta.label.toLowerCase()} at once.`}</p>
          </div>
          <button className="icon-button" onClick={onClose} data-testid="import-close-button"><X size={18} /></button>
        </div>

        {stage === "choose" && (
          <div className="import-body">
            <div className="import-template">
              <div>
                <strong>Need the format?</strong>
                <p>Columns: {fields.map((f) => f.key).join(", ")}</p>
              </div>
              <button className="secondary-button" onClick={downloadTemplate} data-testid="import-template-button">
                <Download size={14} /> Download template
              </button>
            </div>
            <button type="button" className="import-dropzone" onClick={() => inputRef.current?.click()} data-testid="import-dropzone">
              <UploadCloud size={26} />
              <strong>{busy ? "Reading file…" : "Click to choose a CSV file"}</strong>
              <small>Up to 5 MB · .csv · headers in the first row</small>
            </button>
            <input ref={inputRef} type="file" accept=".csv,text/csv" hidden data-testid="import-file-input" onChange={onFile} />
          </div>
        )}

        {stage === "map" && (
          <div className="import-body">
            <div className="import-filename"><FileSpreadsheet size={15} /> {file?.name} · {totalRows} row{totalRows === 1 ? "" : "s"}</div>
            <div className="import-mapping" data-testid="import-mapping">
              {fields.map((field) => (
                <label key={field.key} className="import-map-row">
                  <span>{field.label}{field.required && <em> *</em>}</span>
                  <select
                    data-testid={`import-map-${field.key}`}
                    value={mapping[field.key] || ""}
                    onChange={(e) => setMapping({ ...mapping, [field.key]: e.target.value })}
                  >
                    <option value="">— Not mapped —</option>
                    {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                  </select>
                </label>
              ))}
            </div>
            {sampleRows.length > 0 && (
              <div className="import-preview-table">
                <p className="eyebrow">PREVIEW (first {sampleRows.length})</p>
                <div className="table-wrap">
                  <table>
                    <thead><tr>{fields.map((f) => <th key={f.key}>{f.label}</th>)}</tr></thead>
                    <tbody>
                      {sampleRows.map((row, index) => (
                        <tr key={index}>
                          {fields.map((f) => <td key={f.key}>{mapping[f.key] ? String(row[mapping[f.key]] ?? "") : "—"}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {stage === "done" && result && (
          <div className="import-body">
            <div className="import-result" data-testid="import-result">
              <p><strong>{result.imported}</strong> imported · <strong>{result.skipped}</strong> skipped</p>
              {result.errors?.length > 0 && <ul>{result.errors.map((error, index) => <li key={index}>{error}</li>)}</ul>}
            </div>
          </div>
        )}

        <div className="modal-actions">
          {stage === "map" && (
            <button type="button" className="secondary-button" onClick={() => { setStage("choose"); setFile(null); }} data-testid="import-back-button">
              <ArrowLeft size={14} /> Back
            </button>
          )}
          <button type="button" className="secondary-button" onClick={onClose}>{stage === "done" ? "Done" : "Cancel"}</button>
          {stage === "map" && (
            <button className="primary-button" disabled={busy || !mapping.name} onClick={runImport} data-testid="import-upload-button">
              {busy ? "Importing…" : `Import ${totalRows} row${totalRows === 1 ? "" : "s"}`}
            </button>
          )}
        </div>
      </section>
    </div>
  );
}
